import { create } from "zustand";
import apiService from "../services/apiService";

const useLeadStore = create((set, get) => ({
  // State
  leads: [],
  currentLead: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
  filters: {
    status: null,
    assignedTo: null,
    source: null,
    priority: null,
    search: null
  },
  stats: null,

  // Actions
  fetchLeads: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const queryParams = { ...get().filters, ...params };
      const response = await apiService.get("/leads", { params: queryParams });
      const { leads, pagination } = response.data.data;

      set({
        leads,
        pagination,
        isLoading: false,
        error: null,
      });

      return { success: true, leads, pagination };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch leads";
      set({
        leads: [],
        isLoading: false,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  fetchLeadById: async (leadId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.get(`/leads/${leadId}`);
      const lead = response.data.data;

      set({
        currentLead: lead,
        isLoading: false,
        error: null,
      });

      return { success: true, data: lead };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch lead";
      set({
        currentLead: null,
        isLoading: false,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  createLead: async (leadData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post("/leads", leadData);
      const newLead = response.data.data;

      set((state) => ({
        leads: [newLead, ...state.leads],
        isLoading: false,
        error: null,
      }));

      return { success: true, lead: newLead };
    } catch (error) {
      const details = error.response?.data?.details;
      const messages =
        Array.isArray(details) && details.length > 0
          ? details
          : [error.response?.data?.message || "Failed to create lead"];

      set({
        isLoading: false,
        errorMessages: messages, // store full array in state
      });

      return { success: false, error: messages[0] };
    }
  },

  updateLead: async (leadId, leadData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.put(`/leads/${leadId}`, leadData);
      const updatedLead = response.data.data;

      set((state) => ({
        leads: state.leads.map((lead) =>
          lead._id === leadId ? updatedLead : lead
        ),
        currentLead:
          state.currentLead?._id === leadId ? updatedLead : state.currentLead,
        isLoading: false,
        error: null,
      }));

      return { success: true, lead: updatedLead };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update lead";
      set({
        isLoading: false,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  deleteLead: async (leadId) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.delete(`/leads/${leadId}`);

      set((state) => ({
        leads: state.leads.filter((lead) => lead._id !== leadId),
        currentLead:
          state.currentLead?._id === leadId ? null : state.currentLead,
        isLoading: false,
        error: null,
      }));

      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete lead";
      set({
        isLoading: false,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  addNote: async (leadId, noteContent) => {
    try {
      const response = await apiService.post(`/leads/${leadId}/notes`, {
        content: noteContent,
      });
      const note = response.data.data;

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                notes: [...(state.currentLead.notes || []), note],
              }
            : state.currentLead,
      }));

      return { success: true, note };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to add note";
      return { success: false, error: errorMessage };
    }
  },

  updateNote: async (leadId, noteId, noteContent) => {
    try {
      const response = await apiService.put(
        `/leads/${leadId}/notes/${noteId}`,
        { content: noteContent }
      );
      const updatedNote = response.data.data;

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                notes:
                  state.currentLead.notes?.map((note) =>
                    note._id === noteId ? updatedNote : note
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true, note: updatedNote };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to update note";
      return { success: false, error: errorMessage };
    }
  },

  deleteNote: async (leadId, noteId) => {
    try {
      await apiService.delete(`/leads/${leadId}/notes/${noteId}`);

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                notes:
                  state.currentLead.notes?.filter(
                    (note) => note._id !== noteId
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete note";
      return { success: false, error: errorMessage };
    }
  },

  addRequirement: async (leadId, requirementData) => {
    try {
      const response = await apiService.post(
        `/leads/${leadId}/requirements`,
        requirementData
      );

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                requirements: [
                  ...(state.currentLead.requirements || []),
                  response.data.data,
                ],
              }
            : state.currentLead,
      }));

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to add requirement",
      };
    }
  },

  updateRequirement: async (leadId, requirementId, updateData) => {
    try {
      const response = await apiService.put(
        `/leads/${leadId}/requirements/${requirementId}`,
        updateData
      );

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                requirements:
                  state.currentLead.requirements?.map((req) =>
                    req._id === requirementId ? response.data.data : req
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update requirement",
      };
    }
  },

  deleteRequirement: async (leadId, requirementId) => {
    try {
      await apiService.delete(`/leads/${leadId}/requirements/${requirementId}`);

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                requirements:
                  state.currentLead.requirements?.filter(
                    (req) => req._id !== requirementId
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete requirement",
      };
    }
  },

  addActivity: async (leadId, activityData) => {
    try {
      const response = await apiService.post(
        `/leads/${leadId}/activities`,
        activityData
      );
      const activity = response.data.data;

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                activities: [...(state.currentLead.activities || []), activity],
              }
            : state.currentLead,
      }));

      return { success: true, activity };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to add activity";
      return { success: false, error: errorMessage };
    }
  },

  // Add meeting with optional follow-up meeting
  addMeeting: async (leadId, meetingData, followUpData = null) => {
    try {
      // If we have follow-up data, send it along with the meeting data
      const payload = followUpData ? { meetingData, followUpData } : meetingData;
      
      const response = await apiService.post(
        `/leads/${leadId}/meetings`,
        payload
      );

      // Since the backend might have created multiple meetings (main + follow-up),
      // we'll just refresh the lead data to get all meetings
      await get().fetchLeadById(leadId);

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to add meeting",
      };
    }
  },

  // Update meeting
  updateMeeting: async (leadId, meetingId, updateData) => {
    try {
      const response = await apiService.put(
        `/leads/${leadId}/meetings/${meetingId}`,
        updateData
      );

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                meetings:
                  state.currentLead.meetings?.map((meeting) =>
                    meeting._id === meetingId ? response.data.data : meeting
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update meeting",
      };
    }
  },

  // Delete meeting
  deleteMeeting: async (leadId, meetingId) => {
    try {
      await apiService.delete(`/leads/${leadId}/meetings/${meetingId}`);

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                meetings:
                  state.currentLead.meetings?.filter(
                    (meeting) => meeting._id !== meetingId
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete meeting",
      };
    }
  },

  // Add call
  addCall: async (leadId, callData) => {
    try {
      const response = await apiService.post(
        `/leads/${leadId}/calls`,
        callData
      );

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                calls: [...(state.currentLead.calls || []), response.data.data],
              }
            : state.currentLead,
      }));

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to add call",
      };
    }
  },

  // Update call
  updateCall: async (leadId, callId, updateData) => {
    try {
      const response = await apiService.put(
        `/leads/${leadId}/calls/${callId}`,
        updateData
      );

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                calls:
                  state.currentLead.calls?.map((call) =>
                    call._id === callId ? response.data.data : call
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update call",
      };
    }
  },

  // Delete call
  deleteCall: async (leadId, callId) => {
    try {
      await apiService.delete(`/leads/${leadId}/calls/${callId}`);

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                calls:
                  state.currentLead.calls?.filter(
                    (call) => call._id !== callId
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete call",
      };
    }
  },

  // Add document
  addDocument: async (leadId, documentData) => {
    try {
      const response = await apiService.post(
        `/leads/${leadId}/documents`,
        documentData
      );

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                documents: [
                  ...(state.currentLead.documents || []),
                  response.data.data,
                ],
              }
            : state.currentLead,
      }));

      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to add document",
      };
    }
  },

  // Delete document
  deleteDocument: async (leadId, documentId) => {
    try {
      await apiService.delete(`/leads/${leadId}/documents/${documentId}`);

      set((state) => ({
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                documents:
                  state.currentLead.documents?.filter(
                    (doc) => doc._id !== documentId
                  ) || [],
              }
            : state.currentLead,
      }));

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to delete document",
      };
    }
  },

  convertToDeal: async (leadId, dealData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post(
        `/leads/${leadId}/convert-to-deal`,
        dealData
      );
      const deal = response.data.data;

      // Update lead status to converted
      set((state) => ({
        leads: state.leads.map((lead) =>
          lead._id === leadId
            ? { ...lead, convertedToDeal: true, status: "converted" }
            : lead
        ),
        currentLead:
          state.currentLead?._id === leadId
            ? {
                ...state.currentLead,
                convertedToDeal: true,
                status: "converted",
              }
            : state.currentLead,
        isLoading: false,
        error: null,
      }));

      return { success: true, deal };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to convert lead";
      set({
        isLoading: false,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  fetchLeadStats: async () => {
    try {
      const response = await apiService.get("/leads/stats");
      const stats = response.data.data;

      set({ stats });
      return { success: true, stats };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch stats";
      return { success: false, error: errorMessage };
    }
  },

  // Task management
  addTask: async (leadId, taskData) => {
    try {
      const response = await apiService.post(
        `/leads/${leadId}/tasks`,
        taskData
      );
      if (response.data.success) {
        // Refresh the current lead to get updated tasks
        await get().fetchLeadById(leadId);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const details = error.response?.data?.details;
      const errorMessage =
        error.response?.data?.message || "Failed to add task";

      // Handle both array and object formats for validation details
      let errorDetails = null;
      if (Array.isArray(details) && details.length > 0) {
        errorDetails = details;
      } else if (details && typeof details === 'object') {
        // Convert object of errors to array of messages
        errorDetails = Object.values(details).map(err => err.message || err);
      }

      return {
        success: false,
        error: errorMessage,
        details: errorDetails
      };
    }
  },

  updateTask: async (leadId, taskId, taskData) => {
    try {
      const response = await apiService.put(
        `/leads/${leadId}/tasks/${taskId}`,
        taskData
      );
      if (response.data.success) {
        // Refresh the current lead to get updated tasks
        await get().fetchLeadById(leadId);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const details = error.response?.data?.details;
      const errorMessage =
        error.response?.data?.message || "Failed to update task";

      // Handle both array and object formats for validation details
      let errorDetails = null;
      if (Array.isArray(details) && details.length > 0) {
        errorDetails = details;
      } else if (details && typeof details === 'object') {
        // Convert object of errors to array of messages
        errorDetails = Object.values(details).map(err => err.message || err);
      }

      return {
        success: false,
        error: errorMessage,
        details: errorDetails
      };
    }
  },

  deleteTask: async (leadId, taskId) => {
    try {
      const response = await apiService.delete(
        `/leads/${leadId}/tasks/${taskId}`
      );
      if (response.data.success) {
        // Refresh the current lead to get updated tasks
        await get().fetchLeadById(leadId);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete task";
      return { success: false, error: errorMessage };
    }
  },

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  bulkAssignLeads: async (leadIds, assignToUserId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post('/leads/bulk/assign', {
        leadIds,
        assignToUserId
      });

      // Refresh leads to get updated data
      const currentFilters = get().filters;
      const currentPagination = get().pagination;
      
      await get().fetchLeads({
        page: currentPagination.page,
        limit: currentPagination.limit,
        ...currentFilters
      });

      set({ isLoading: false });
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to assign leads';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  clearFilters: () => set({
    filters: {
      status: null,
      assignedTo: null,
      source: null,
      priority: null,
      search: null
    }
  }),

  clearError: () => set({ error: null }),

  clearCurrentLead: () => set({ currentLead: null }),
}));

export default useLeadStore;
